import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaUpload } from 'react-icons/fa';
import './ProductForm.css';

function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [currentVersion, setCurrentVersion] = useState('');
  const [price, setPrice] = useState('');
  const [type, setType] = useState('expert_advisor');
  const [videoLinks, setVideoLinks] = useState('');
  const [setFileSymbols, setSetFileSymbols] = useState('');
  const [setFileNotes, setSetFileNotes] = useState('');
  const [file, setFile] = useState(null);
  const [image, setImage] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [setFiles, setSetFiles] = useState([]);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchProduct = useCallback(async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const product = response.data;
      setName(product.name || '');
      setDescription(product.description || '');
      setReleaseNotes(product.releaseNotes || '');
      setCurrentVersion(product.currentVersion || '');
      setPrice(product.price || '');
      setType(product.type || 'expert_advisor');
      setVideoLinks(product.videoLinks || '');
      setSetFileSymbols(product.setFileSymbols || '');
      setSetFileNotes(product.setFileNotes || '');
    } catch (err) {
      setError('Failed to fetch product.');
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSaving(true);

    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description);
    formData.append('releaseNotes', releaseNotes);
    formData.append('currentVersion', currentVersion);
    formData.append('price', price);
    formData.append('type', type);
    formData.append('videoLinks', videoLinks);
    formData.append('setFileSymbols', setFileSymbols);
    formData.append('setFileNotes', setFileNotes);
    if (file) formData.append('file', file);
    if (image) formData.append('image', image);
    screenshots.forEach((item) => formData.append('screenshots', item));
    setFiles.forEach((item) => formData.append('setFiles', item));

    try {
      const token = localStorage.getItem('adminToken');
      if (id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/products/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/products`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      }
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save product.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-form-page page-shell">
      <div className="admin-form-shell">
        <div className="admin-form-header">
          <div>
            <span className="tag">Admin Product</span>
            <h1 className="page-title">{id ? 'Edit product' : 'Add product'}</h1>
            <p className="page-lead">
              Upload files, manage markdown content, and keep catalog metadata consistent with the storefront.
            </p>
          </div>
          <Link to="/admin" className="ghost-cta">Back to Admin</Link>
        </div>

        <div className="admin-form-layout">
          <form className="section-card admin-form-card" onSubmit={handleSubmit}>
            {error && <p className="error-text">{error}</p>}

            <div className="form-grid two-column">
              <label className="admin-field">
                <span>Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Example: VIX Breakout Engine"
                />
              </label>

              <label className="admin-field">
                <span>Price</span>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </label>
            </div>

            <div className="form-grid two-column">
              <label className="admin-field">
                <span>Type</span>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="expert_advisor">Expert Advisor</option>
                  <option value="indicator">Indicator</option>
                  <option value="source_code">Source Code</option>
                </select>
              </label>

              <label className="admin-field">
                <span>Video links</span>
                <input
                  type="text"
                  value={videoLinks}
                  onChange={(e) => setVideoLinks(e.target.value)}
                  placeholder="Comma-separated YouTube or Vimeo links"
                />
              </label>
            </div>

            <label className="admin-field">
              <span>Description</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="14"
                placeholder="Write the product description in Markdown."
              />
            </label>

            <div className="form-grid two-column">
              <label className="admin-field">
                <span>Current version</span>
                <input
                  type="text"
                  value={currentVersion}
                  onChange={(e) => setCurrentVersion(e.target.value)}
                  placeholder="v1.0.0"
                />
              </label>
            </div>

            <label className="admin-field">
              <span>Release notes</span>
              <textarea
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
                rows="10"
                placeholder="Add version notes in Markdown. Example: v1.2.0 - Fixed trailing stop behavior."
              />
            </label>

            <div className="form-grid two-column">
              <label className="admin-field">
                <span>Set file symbols</span>
                <input
                  type="text"
                  value={setFileSymbols}
                  onChange={(e) => setSetFileSymbols(e.target.value)}
                  placeholder="XAUUSD, EURUSD, NAS100"
                />
              </label>

              <label className="admin-field">
                <span>Set file notes</span>
                <input
                  type="text"
                  value={setFileNotes}
                  onChange={(e) => setSetFileNotes(e.target.value)}
                  placeholder="Pipe-separated notes | one per set file"
                />
              </label>
            </div>

            <div className="admin-upload-grid">
              <label className="admin-upload-field">
                <span><FaUpload /> Primary file</span>
                <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>

              <label className="admin-upload-field">
                <span><FaUpload /> Cover image</span>
                <input type="file" onChange={(e) => setImage(e.target.files?.[0] || null)} />
              </label>

              <label className="admin-upload-field">
                <span><FaUpload /> Screenshots / videos</span>
                <input type="file" multiple onChange={(e) => setScreenshots(Array.from(e.target.files || []))} />
              </label>

              <label className="admin-upload-field">
                <span><FaUpload /> Set files</span>
                <input type="file" multiple onChange={(e) => setSetFiles(Array.from(e.target.files || []))} />
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Product'}
              </button>
              <Link to="/admin" className="ghost-cta">Cancel</Link>
            </div>
          </form>

          <aside className="section-card admin-form-sidecard">
            <h2>Publishing notes</h2>
            <ul className="admin-hint-list">
              <li>Keep catalog titles short. Put detailed structure inside the product page description.</li>
              <li>Markdown headings in cards are now normalized, but compact descriptions still read best with short paragraphs.</li>
              <li>Use one set-file symbol and one note per file, in the same order as the uploaded files.</li>
              <li>Large screenshots are fine; the storefront now constrains them correctly.</li>
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default ProductForm;







